// Login: pick an operator, then enter the PIN. Single screen with two states.

import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { authApi, type Operator } from "./api";
import { authKeys, useOperators } from "./hooks";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { Card } from "../../components/Card";
import { Logo } from "../../components/Logo";
import { ApiError } from "../../lib/api";

export function LoginScreen() {
  const [selected, setSelected] = useState<Operator | null>(null);
  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);

  const operators = useOperators(true);
  const qc = useQueryClient();

  useEffect(() => {
    if (operators.data && operators.data.length > 0 && !selected) {
      setSelected(operators.data[0]);
    }
  }, [operators.data, selected]);

  const login = useMutation({
    mutationFn: authApi.login,
    onSuccess: (op) => {
      qc.setQueryData(authKeys.me, op);
    },
    onError: (e) => {
      if (e instanceof ApiError && e.status === 401) {
        setError("PIN incorrecto.");
      } else {
        setError("No se pudo iniciar sesión.");
      }
      setPin("");
    },
  });

  if (operators.isLoading) return null;
  if (operators.data && operators.data.length === 0) {
    return (
      <main style={centerStyle}>
        <Logo height={40} />
        <p>No hay operadores. Pedile al admin que cree uno.</p>
      </main>
    );
  }

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!selected) return;
    if (!/^\d{4,6}$/.test(pin)) return setError("PIN de 4 a 6 dígitos.");
    login.mutate({ operatorId: selected.id, pin });
  };

  return (
    <main style={centerStyle} className="login-screen">
      <div
        className="login-card"
        style={{
          width: "100%",
          maxWidth: 380,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "var(--space-6)",
        }}
      >
        <div className="login-brand"><Logo height={34} /><span>Punto de venta</span></div>
        <div className="login-heading"><p className="eyebrow">Bienvenido</p><h1 style={titleStyle}>Ingresar</h1><span>Elegí tu operador e ingresá el PIN.</span></div>

        <div
          role="radiogroup"
          aria-label="Operador"
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: "var(--space-2)",
          }}
        >
          <span style={labelStyle}>Operador</span>
          {operators.data?.map((op) => (
            <Card
              key={op.id}
              selected={selected?.id === op.id}
              onClick={() => {
                setSelected(op);
                setPin("");
                setError(null);
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
                <Avatar name={op.name} />
                <span style={{ fontSize: "var(--font-size-16)", fontWeight: 500 }}>{op.name}</span>
              </div>
            </Card>
          ))}
        </div>

        <form
          onSubmit={submit}
          style={{ width: "100%", display: "flex", flexDirection: "column", gap: "var(--space-4)" }}
        >
          <Input
            label={`PIN de ${selected?.name ?? ""}`}
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
            type="password"
            inputMode="numeric"
            autoComplete="off"
            maxLength={6}
            placeholder="••••"
          />
          {error && (
            <span
              style={{ color: "var(--color-danger)", fontSize: "var(--font-size-14)" }}
              role="alert"
            >
              {error}
            </span>
          )}
          <Button
            type="submit"
            variant="primary"
            fullWidth
            size="lg"
            disabled={login.isPending}
          >
            {login.isPending ? "Entrando..." : "Entrar"}
          </Button>
        </form>
      </div>
    </main>
  );
}

function Avatar({ name }: { name: string }) {
  const initial = name.trim().charAt(0).toUpperCase() || "?";
  return (
    <div
      aria-hidden="true"
      style={{
        width: 36,
        height: 36,
        borderRadius: "var(--radius-pill)",
        background: "var(--surface-sunken)",
        color: "var(--text-primary)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 600,
        fontSize: "var(--font-size-14)",
        flex: "0 0 auto",
      }}
    >
      {initial}
    </div>
  );
}

const centerStyle: React.CSSProperties = {
  minHeight: "100dvh",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: "var(--space-6)",
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "var(--font-size-22)",
  fontWeight: 500,
  letterSpacing: "-0.01em",
};

const labelStyle: React.CSSProperties = {
  fontSize: "var(--font-size-14)",
  color: "var(--text-secondary)",
  fontWeight: 500,
};
